import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Eye,
  Scale,
  FileText,
  Users,
  Layers,
  Network,
  Mountain,
  Coins,
  Landmark,
  CircleCheck,
  XCircle,
  Workflow,
  Database,
  KeyRound,
  BarChart3,
  Sparkles,
} from 'lucide-react';
import { colors } from '../styles/colors';
import LiveReserveData from '../components/LiveReserveData';
import { useFillout } from '../hooks/useFillout';

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

const ReserveArchitecture = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const navigate = useNavigate();
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  // §2 core principles
  const principles = [
    { Icon: ShieldCheck, label: 'Verification' },
    { Icon: Eye, label: 'Transparency' },
    { Icon: Scale, label: 'Governance' },
    { Icon: Layers, label: 'Visibility' },
    { Icon: Users, label: 'Participation' },
  ];

  // §3 participants
  const participants = [
    { Icon: Mountain, title: 'Mines', body: 'Contribute verified resources into the reserve framework.', href: '/mines' },
    { Icon: Coins, title: 'Bullion Providers', body: 'Contribute physical bullion into the reserve.', href: '/bullion-providers' },
    { Icon: Landmark, title: 'Liquidity Providers', body: 'Support liquidity participation through structured frameworks.', href: '/liquidity-providers' },
  ];

  // §4 framework process
  const processSteps = [
    { step: '01', title: 'Verification', body: 'Assets undergo review and assessment.', Icon: ShieldCheck },
    { step: '02', title: 'Reserve Participation', body: 'Approved assets enter the reserve framework.', Icon: Layers },
    { step: '03', title: 'Reserve Visibility', body: 'Assets become visible through reserve reporting and reserve data.', Icon: Eye },
    { step: '04', title: 'Participant Interaction', body: 'Participants engage through structured ecosystem pathways.', Icon: Network },
    { step: '05', title: 'Ongoing Reporting', body: 'Transparency is maintained through reserve visibility and reporting.', Icon: BarChart3 },
  ];

  // §5 three layers
  const layers = [
    {
      tag: 'The Ecosystem Layer',
      name: 'Arizore',
      Icon: Network,
      bullets: ['Mines', 'Bullion Providers', 'Liquidity Providers', 'Strategic Partners', 'Infrastructure Participants'],
      purpose: 'Creates the environment where participation occurs.',
    },
    {
      tag: 'The Reserve Layer',
      name: 'AZ Reserves',
      Icon: Database,
      bullets: ['Verification', 'Transparency', 'Governance', 'Reporting', 'Reserve Visibility'],
      purpose: 'The foundation of the ecosystem.',
      featured: true,
    },
    {
      tag: 'The Interaction Layer',
      name: 'Digital Instruments',
      Icon: KeyRound,
      bullets: ['Access', 'Participation', 'Administration', 'Ecosystem Interaction'],
      purpose: 'The access layer of the reserve.',
    },
  ];

  // §8 governance
  const governance = [
    { Icon: ShieldCheck, title: 'Verification Standards', body: 'Defined standards supporting reserve integrity.' },
    { Icon: Workflow, title: 'Technical Review', body: 'Independent assessment of assets and documentation.' },
    { Icon: Scale, title: 'Governance Separation', body: 'Corporate, reserve, and marketplace layers operate distinctly.' },
    { Icon: FileText, title: 'Reporting Processes', body: 'Ongoing visibility through reserve composition and reporting.' },
    { Icon: Users, title: 'Ecosystem Partners', body: 'Clearly defined participant roles across the ecosystem.' },
  ];

  // §9 comparison
  const traditional = ['Limited visibility', 'Fragmented information', 'Difficult assessment', 'Unclear reporting', 'Limited transparency'];
  const reserveFramework = ['Transparent visibility', 'Defined structure', 'Ongoing reporting', 'Verification standards', 'Structured participation pathways'];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* §1 HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '94vh', background: 'linear-gradient(180deg, #000000 0%, #03090C 55%, #07141A 100%)' }}>
        <img
          src="/architecture-hero.webp"
          alt=""
          aria-hidden="true"
          data-testid="ra-hero-image"
          className={`absolute inset-0 w-full h-full object-cover ra-hero-drift transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{ objectPosition: 'center center' }}
          loading="eager"
          decoding="async"
        />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.30) 38%, rgba(0,0,0,0.50) 72%, rgba(0,0,0,0.94) 100%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(58% 62% at 50% 50%, rgba(0,128,128,0.12) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[94vh] pt-32 pb-20 sm:pt-36 sm:pb-24">
          <div className={`transition-all duration-1000 ease-out transform ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}>
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal, fontFamily: 'inherit' }}>
                <Layers className="w-3.5 h-3.5" strokeWidth={1.5} />
                Reserve Architecture
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-[-0.02em] mb-7 max-w-4xl mx-auto" style={{ color: colors.white, fontFamily: 'inherit', fontWeight: 700 }}>
              THE FRAMEWORK BEHIND THE ECOSYSTEM.
            </h1>
            <p className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-2xl mx-auto mb-12" style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'inherit' }}>
              Connecting verified assets, reserve visibility, and liquidity pathways through a transparent reserve framework.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => navigate('/reserve-data/azg')}
                data-testid="ra-hero-reserve-data-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}`, fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                View Reserve Data
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/')}
                data-testid="ra-hero-ecosystem-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              >
                Explore The Ecosystem
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes ra-hero-drift { 0% { transform: scale(1.04) translate3d(0,0,0); } 50% { transform: scale(1.06) translate3d(-0.5%, -0.5%, 0); } 100% { transform: scale(1.04) translate3d(0,0,0); } }
          .ra-hero-drift { animation: ra-hero-drift 26s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) { .ra-hero-drift { animation: none; } }
        `}</style>
      </section>

      {/* §2 WHY THE RESERVE EXISTS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-1.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.6) 50%, #040404 100%)' }} />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why The Reserve Exists"
            title="CONNECTING ASSETS AND CAPITAL THROUGH TRANSPARENCY."
            sub="Many valuable assets struggle to access liquidity despite holding significant value. The reserve framework was created to connect verified assets, reserve visibility, and ecosystem participants through a structured environment."
          />
          <div className="max-w-3xl mx-auto rounded-2xl p-6 sm:p-8 text-center mb-12 sm:mb-16" style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.07) 0%, rgba(0,128,128,0.02) 100%)', border: `1px solid ${colors.teal}26` }}>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.85 }}>
              The objective is not simply <span style={{ color: colors.white, opacity: 0.55 }}>participation</span>. The objective is <span style={{ color: colors.teal, fontWeight: 500 }}>transparency, structure, and confidence</span>.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {principles.map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`ra-principle-${label.toLowerCase()}`}
                className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §3 WHO PARTICIPATES */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#050505' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Who Participates"
            title="THREE PARTICIPANT GROUPS. ONE RESERVE FRAMEWORK."
            sub="The reserve framework brings together multiple participant groups, each contributing to a connected ecosystem."
          />
          {/* Participant cards (connect down to the reserve) */}
          <div className="grid sm:grid-cols-3 gap-5 lg:gap-6 mb-0">
            {participants.map(({ Icon, title, body, href }) => (
              <button
                key={title}
                onClick={() => navigate(href)}
                data-testid={`ra-participant-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group relative text-left p-7 sm:p-8 rounded-2xl h-full flex flex-col transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}45`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 leading-tight tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm sm:text-base leading-relaxed flex-grow" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                <span className="inline-flex items-center gap-1.5 mt-5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.teal }}>
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            ))}
          </div>

          {/* Connector */}
          <div aria-hidden="true" className="flex justify-center my-0">
            <div className="w-px h-12 sm:h-16" style={{ background: `linear-gradient(180deg, ${colors.teal}55 0%, ${colors.teal}20 100%)` }} />
          </div>

          {/* Reserve Framework hub */}
          <div
            data-testid="ra-participant-reserve-framework"
            className="relative max-w-3xl mx-auto rounded-2xl p-8 sm:p-10 text-center"
            style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.10) 0%, rgba(0,128,128,0.03) 100%)', border: `1px solid ${colors.teal}40`, boxShadow: `0 30px 60px -30px ${colors.teal}40, inset 0 1px 0 rgba(255,255,255,0.05)` }}
          >
            <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}1f`, border: `1px solid ${colors.teal}55` }}>
              <Database className="w-7 h-7" style={{ color: colors.teal }} strokeWidth={1.5} />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-3 tracking-tight" style={{ color: colors.white }}>Reserve Framework</h3>
            <p className="text-sm sm:text-base leading-relaxed max-w-xl mx-auto" style={{ color: colors.white, opacity: 0.72 }}>
              Provides governance, transparency, reporting, and visibility — the foundation that connects every participant group.
            </p>
          </div>
        </div>
      </section>

      {/* §4 HOW THE RESERVE FRAMEWORK WORKS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-2.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.6) 50%, #040404 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How The Framework Works"
            title="A STRUCTURED PROCESS DESIGNED AROUND TRANSPARENCY."
            sub="Assets move through a deliberate process built for verification, visibility, and ongoing reporting."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-4">
            {processSteps.map(({ step, title, body, Icon }, idx) => (
              <div
                key={step}
                data-testid={`ra-process-step-${step}`}
                className="relative p-6 sm:p-7 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${colors.teal}24` }}
              >
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#070707', color: colors.teal, border: `1px solid ${colors.teal}80` }}>{step}</span>
                <div className="mt-3 mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.1em] mb-2 text-center leading-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm leading-relaxed text-center" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                {idx < processSteps.length - 1 && <ArrowRight aria-hidden="true" className="hidden lg:block absolute top-1/2 -right-3 w-5 h-5 -translate-y-1/2" style={{ color: colors.teal, opacity: 0.4 }} strokeWidth={1.5} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §5 ONE ECOSYSTEM. THREE DISTINCT LAYERS. — signature section */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ background: 'linear-gradient(180deg, #050505 0%, #08161B 50%, #050505 100%)' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 30%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Operating Model"
            title="ONE ECOSYSTEM. THREE DISTINCT LAYERS."
            sub="The Arizore ecosystem operates through three connected layers, each serving a different purpose. Understanding these layers explains how assets, reserves, and participation interact."
          />
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {layers.map(({ tag, name, Icon, bullets, purpose, featured }) => (
              <div
                key={name}
                data-testid={`ra-layer-${name.toLowerCase().replace(/\s+/g, '-')}`}
                className="relative flex flex-col rounded-2xl p-7 sm:p-8 transition-all duration-500"
                style={{
                  background: featured
                    ? 'linear-gradient(180deg, rgba(0,128,128,0.12) 0%, rgba(0,128,128,0.03) 100%)'
                    : 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                  border: featured ? `1px solid ${colors.teal}50` : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: featured ? `0 30px 60px -30px ${colors.teal}45, inset 0 1px 0 rgba(255,255,255,0.05)` : 'none',
                }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}16`, border: `1px solid ${colors.teal}45` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.26em] font-medium mb-2" style={{ color: colors.teal, opacity: 0.9 }}>{tag}</span>
                <h3 className="text-xl sm:text-2xl font-bold mb-5 tracking-tight" style={{ color: colors.white }}>{name}</h3>
                <ul className="space-y-2.5 mb-6 flex-grow">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm" style={{ color: colors.white, opacity: 0.72 }}>
                      <CircleCheck className="w-4 h-4 flex-shrink-0" style={{ color: colors.teal }} strokeWidth={1.5} />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="pt-5 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                  <span className="block text-[10px] uppercase tracking-[0.22em] font-medium mb-1.5" style={{ color: colors.white, opacity: 0.4 }}>Purpose</span>
                  <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.8 }}>{purpose}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Strategic takeaway */}
          <div className="mt-12 sm:mt-16 max-w-4xl mx-auto rounded-2xl p-7 sm:p-9 text-center" style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Sparkles className="w-6 h-6 mx-auto mb-4" style={{ color: colors.teal }} strokeWidth={1.5} />
            <p className="text-base sm:text-lg leading-relaxed mb-3" style={{ color: colors.white, opacity: 0.85 }}>
              <span style={{ color: colors.teal, fontWeight: 500 }}>Arizore</span> coordinates participation. <span style={{ color: colors.teal, fontWeight: 500 }}>AZ Reserves</span> provides the foundation. <span style={{ color: colors.teal, fontWeight: 500 }}>Digital Instruments</span> provide interaction.
            </p>
            <p className="text-sm sm:text-base leading-relaxed max-w-2xl mx-auto" style={{ color: colors.white, opacity: 0.6 }}>
              Together they create a transparent ecosystem connecting assets, reserves, and liquidity.
            </p>
          </div>
        </div>
      </section>

      {/* §6 THE ROLE OF DIGITAL INSTRUMENTS */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Role Of Digital Instruments"
            title="THE ACCESS LAYER OF THE RESERVE."
            sub="Digital instruments are the mechanism through which participants interact with the reserve. The reserve remains the foundation — digital instruments act as the access layer, supporting participation, administration, and interaction within the reserve framework."
          />
          <div className="max-w-3xl mx-auto rounded-2xl p-7 sm:p-9 text-center" style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.08) 0%, rgba(0,128,128,0.02) 100%)', border: `1px solid ${colors.teal}30` }}>
            <KeyRound className="w-6 h-6 mx-auto mb-4" style={{ color: colors.teal }} strokeWidth={1.5} />
            <p className="text-base sm:text-lg leading-relaxed mb-2" style={{ color: colors.white, opacity: 0.88 }}>
              The reserve is the <span style={{ color: colors.teal, fontWeight: 500 }}>model</span>. Digital instruments are the <span style={{ color: colors.teal, fontWeight: 500 }}>interaction layer</span>.
            </p>
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.6 }}>
              Reserve growth and digital instrument performance are different concepts.
            </p>
          </div>

          {/* AZG / AZS — the interaction layer (educational, neutral) */}
          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto mt-8">
            <div data-testid="ra-instrument-azg" className="rounded-2xl p-6 sm:p-7" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid #C6A84B33' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#C6A84B14', border: '1px solid #C6A84B45' }}>
                  <Coins className="w-5 h-5" style={{ color: '#F2D675' }} strokeWidth={1.5} />
                </div>
                <span className="text-lg font-bold tracking-tight" style={{ color: colors.white }}>AZG</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
                Gold-linked reserve participation instrument. Supports interaction with the gold reserve framework.
              </p>
            </div>
            <div data-testid="ra-instrument-azs" className="rounded-2xl p-6 sm:p-7" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid #B8BCC233' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#B8BCC214', border: '1px solid #B8BCC245' }}>
                  <Coins className="w-5 h-5" style={{ color: '#EDEDED' }} strokeWidth={1.5} />
                </div>
                <span className="text-lg font-bold tracking-tight" style={{ color: colors.white }}>AZS</span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.7 }}>
                Silver-linked reserve participation instrument. Supports interaction with the silver reserve framework.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* §7 RESERVE TRANSPARENCY — drop-in <LiveReserveData /> UNCHANGED */}
      <section className="relative pt-20 sm:pt-28 pb-4 sm:pb-6" style={{ backgroundColor: '#050505' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Reserve Transparency"
            title="VISIBILITY SUPPORTS CONFIDENCE."
            sub="Transparency is a core principle of the reserve framework. Reserve data provides visibility into reserve composition, reserve value, and reserve activity."
          />
        </div>
        <LiveReserveData />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 sm:mt-12 flex justify-center">
          <button
            onClick={() => navigate('/reserve-data/azg')}
            data-testid="ra-reserve-cta"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
            style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Explore Reserve Data
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* §8 GOVERNANCE & VERIFICATION */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-3.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.6) 50%, #040404 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Governance & Verification"
            title="TRUST THROUGH STRUCTURE."
            sub="The reserve framework is supported through verification standards, governance processes, reporting requirements, and ecosystem partners — confidence through transparency and clearly defined participant roles."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-12">
            {governance.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`ra-governance-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.1em] mb-2 leading-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-xs sm:text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §9 WHY THE STRUCTURE MATTERS */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#050505' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why The Structure Matters"
            title="DESIGNED FOR TRANSPARENCY, NOT COMPLEXITY."
            sub="The reserve framework replaces fragmented, opaque environments with structure, visibility, and ongoing reporting."
          />
          <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
            {/* Traditional */}
            <div
              data-testid="ra-compare-traditional"
              className="rounded-2xl p-7 sm:p-9"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <span className="block text-[10px] uppercase tracking-[0.26em] font-medium mb-5" style={{ color: colors.white, opacity: 0.45 }}>Traditional Environment</span>
              <ul className="space-y-3.5">
                {traditional.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm sm:text-base" style={{ color: colors.white, opacity: 0.55 }}>
                    <XCircle className="w-5 h-5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }} strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {/* Reserve Framework */}
            <div
              data-testid="ra-compare-framework"
              className="rounded-2xl p-7 sm:p-9"
              style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.10) 0%, rgba(0,128,128,0.03) 100%)', border: `1px solid ${colors.teal}40`, boxShadow: `0 30px 60px -30px ${colors.teal}40` }}
            >
              <span className="block text-[10px] uppercase tracking-[0.26em] font-medium mb-5" style={{ color: colors.teal, opacity: 0.9 }}>Reserve Framework</span>
              <ul className="space-y-3.5">
                {reserveFramework.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm sm:text-base" style={{ color: colors.white, opacity: 0.9 }}>
                    <CircleCheck className="w-5 h-5 flex-shrink-0" style={{ color: colors.teal }} strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* §10 WHERE DO YOU FIT */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-1.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.7) 50%, #040404 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Where Do You Fit?"
            title="EXPLORE THE ECOSYSTEM."
            sub="Different participant groups interact with the reserve framework in different ways."
          />
          <div className="grid sm:grid-cols-3 gap-5 lg:gap-6">
            {participants.map(({ Icon, title, href }) => (
              <button
                key={title}
                onClick={() => navigate(href)}
                data-testid={`ra-pathway-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group flex flex-col items-start text-left p-7 sm:p-8 rounded-2xl transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}45`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-4 tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.teal }}>
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* §11 GET IN TOUCH */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-2.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.75) 50%, #050505 100%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Get In Touch"
            title="EXPLORE RESERVE PARTICIPATION."
            sub="Speak with the Arizore team to understand reserve participation, eligibility requirements, and ecosystem pathways."
          />
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={openFilloutPopup}
              data-testid="ra-contact-getintouch-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Get In Touch
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/')}
              data-testid="ra-contact-ecosystem-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              Explore The Ecosystem
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReserveArchitecture;
